/**
 * AreaShowcase — brandowany panel logowania z kafelkami obszarów produktu.
 *
 * Wzorowany na ekranie logowania ITE Workflow365: lewy panel z gradientem akcentu,
 * logo + hasło, siatka 2-kolumnowa kafelków obszarów. Klik w kafelek → modal z
 * gradientowym nagłówkiem (ikona + opis obszaru) i listą modułów (nazwa + opis).
 *
 * Struktura obszarów (id, ikona, gradient, klucze modułów) z `areas.ts`; cała treść
 * user-facing przez i18n (`public.showcase.*`). Animacje na CSS (hover-lift kafelków,
 * fade modala) — bez framer-motion. Reużywalny; obecnie na `PublicLogin`.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, X } from 'lucide-react';

import { IconButton } from '@/components/IconButton';
import { Overlay } from '@/components/Overlay';

import { LOGIN_AREAS, type LoginArea } from './areas';

/**
 * Marka produktu na ekranie logowania. Świadomie trzymana jako stała w kodzie
 * (decyzja produktowa 2026-07-22): nazwa NIE idzie do i18n — jest jedna dla
 * wszystkich instancji. Docelowo klienci premium będą mogli ją nadpisać
 * (white-label per-instancja) — patrz docs/OPEN_QUESTIONS.md. Wtedy ta stała
 * zostanie zastąpiona wartością z konfiguracji instancji.
 */
const BRAND = 'ITE Workflow365';

/**
 * Obszary pokazywane na ekranie logowania (parytet z Figmą): bez „Obieg Dokumentów"
 * (na makiecie go nie ma), pierwszy — „Finanse i Księgowość" — renderowany na całą
 * szerokość, reszta w siatce 2-kolumnowej.
 */
const SHOWCASE_AREAS = LOGIN_AREAS.filter((a) => a.id !== 'obieg-dokumentow');

/**
 * Okno obszaru na rdzeniu `Overlay` (tło, Esc, fokus, blokada przewijania). Animacja wejścia
 * (`show`): tło rozjaśnia się kolorem (nie `opacity`, bo wygasiłoby też panel), panel skaluje się
 * i pojawia. Komponent pre-login — bez kontekstu zalogowanego użytkownika.
 */
function AreaModal({ area, onClose }: { area: LoginArea; onClose: () => void }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  useEffect(() => {
    setShow(true);
  }, []);

  const Icon = area.Icon;
  const base = `public.showcase.areas.${area.id}`;
  const title = t(`${base}.title`);

  return (
    <Overlay
      open
      onClose={onClose}
      ariaLabel={title}
      backdropClassName={`z-50 flex items-center justify-center p-4 transition-all duration-200 ${
        show ? 'bg-gray-900/60 backdrop-blur-sm' : 'bg-gray-900/0'
      }`}
      panelClassName={`relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-black/5 bg-white shadow-2xl transition-all duration-200 dark:border-white/10 dark:bg-gray-800 ${
        show ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      }`}
    >
      <div className={`relative bg-gradient-to-br p-6 text-white ${area.gradient}`}>
        <IconButton
          size="sm"
          onClick={onClose}
          aria-label={t('public.showcase.closeAria')}
          className="absolute right-4 top-4 h-8 w-8 bg-white/20 text-white hover:bg-white/30 hover:text-white"
        >
          <X aria-hidden="true" />
        </IconButton>
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
          <Icon className="h-6 w-6" />
        </div>
        <h3 className="text-xl font-bold">{title}</h3>
        <p className="mt-1 text-sm text-white/85">{t(`${base}.description`)}</p>
      </div>
      <div className="overflow-y-auto p-6">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
          {t('public.showcase.modulesTitle', { n: area.moduleKeys.length })}
        </div>
        <ul className="space-y-2.5">
          {area.moduleKeys.map((key) => (
            <li
              key={key}
              className="flex gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-700/50 dark:bg-gray-900/50"
            >
              <span
                className={`mt-1 h-2 w-2 flex-none rounded-full bg-gradient-to-br ${area.gradient}`}
                aria-hidden="true"
              />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t(`${base}.modules.${key}.name`)}
                </div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {t(`${base}.modules.${key}.desc`)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Overlay>
  );
}

function AreaTile({
  area,
  onOpen,
  className = '',
}: {
  area: LoginArea;
  onOpen: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const Icon = area.Icon;
  const base = `public.showcase.areas.${area.id}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`group relative flex items-start gap-3.5 rounded-xl border border-gray-200 bg-white p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md ${className}`}
      data-testid={`login-area-${area.id}`}
    >
      <div
        className={`flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br shadow-sm ${area.gradient}`}
      >
        <Icon className="h-[22px] w-[22px] text-white" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-semibold leading-tight text-gray-900">
          {t(`${base}.title`)}
        </span>
        <span className="mt-1 line-clamp-2 block text-xs leading-snug text-gray-500">
          {t(`${base}.tagline`)}
        </span>
      </div>
      <ArrowUpRight className="h-4 w-4 flex-none text-gray-300 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gray-500" />
    </button>
  );
}

/**
 * Prawy panel ekranu logowania (parytet z Figmą „Logowanie — ITE Workflow365"): BIAŁE tło,
 * nagłówek + siatka jasnych kafli obszarów (kolorowa ikona + nazwa + tagline). Klik → modal z
 * modułami obszaru. Logo/marka żyją teraz na karcie logowania po lewej, nie tutaj.
 */
export function AreaShowcase({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const [activeArea, setActiveArea] = useState<LoginArea | null>(null);

  return (
    <div className={`relative flex-col overflow-hidden bg-white p-10 xl:p-12 ${className}`}>
      {/* Nagłówek + kafle wyśrodkowane w PIONIE (parytet z formularzem po lewej);
          stopka pinnowana na dole. */}
      <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-center overflow-y-auto py-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="text-center">
          <h2 className="text-[26px] font-bold leading-tight text-gray-900 xl:text-3xl">
            {t('public.showcase.heading')}
          </h2>
          <p className="mt-2 text-sm text-gray-500 xl:text-base">
            {t('public.showcase.subheading')}
          </p>
        </div>

        <div className="mt-16 grid grid-cols-2 gap-4 xl:mt-20">
          {SHOWCASE_AREAS.map((area, i) => (
            <AreaTile
              key={area.id}
              area={area}
              onOpen={() => setActiveArea(area)}
              className={i === 0 ? 'col-span-2' : ''}
            />
          ))}
        </div>
      </div>

      <div className="relative z-10 pt-5 text-xs text-gray-400">
        {t('public.showcase.copyright', { brand: BRAND })}
      </div>

      {activeArea && <AreaModal area={activeArea} onClose={() => setActiveArea(null)} />}
    </div>
  );
}
